import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, Star, StarOff, Check, X, ExternalLink,
  Eye, EyeOff, GripVertical, Building2, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
}

const INDUSTRIES = [
  'Education', 'Higher Education', 'Government', 'Military / Federal',
  'Chamber / Community', 'Construction / Trade', 'Manufacturing',
  'Entrepreneurship / Veterans', 'Education / Youth', 'Hospitality',
  'Marketing / Branding', 'Energy / Electrical', 'Workforce / Community',
  'Other',
];

const WORKSHOP_CATEGORIES = [
  'Leadership Development',
  'Team Building & Culture Alignment',
  'Entrepreneurship & Business Growth',
  'AI & Technology Integration',
  'Youth Leadership & Career Development',
  'Assessments & Diagnostics',
];

const EMPTY_FORM = {
  org_name: '',
  short_name: '',
  logo_url: '',
  website_url: '',
  industry: '',
  workshop_category: '',
  engagement_title: '',
  description: '',
  outcome: '',
  testimonial_quote: '',
  testimonial_author: '',
  audience: '',
  hero_image: '',
  video_url: '',
  contact_name: '',
  featured: false,
  active: true,
  display_order: 100,
};

export default function CaseStudiesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {} | item
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/case-studies`, { headers: getAuthHeaders() });
      setItems(data.items || []);
    } catch (err) {
      console.error('[CaseStudies] Fetch failed:', err?.message);
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const startCreate = () => setEditing({ ...EMPTY_FORM });
  const startEdit = (item) => setEditing({ ...item });
  const cancelEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing.org_name?.trim()) {
      toast.error('Organization Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const { id, created_at, updated_at, ...payload } = editing;
        await axios.patch(`${API}/api/admin/case-studies/${id}`, payload, { headers: getAuthHeaders() });
        toast.success('Case study updated');
      } else {
        await axios.post(`${API}/api/admin/case-studies`, editing, { headers: getAuthHeaders() });
        toast.success('Case study created');
      }
      setEditing(null);
      fetchItems();
    } catch (err) {
      console.error('[CaseStudies] Save failed:', err?.response?.data || err?.message);
      toast.error('Save failed: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.org_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/case-studies/${item.id}`, { headers: getAuthHeaders() });
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      console.error('[CaseStudies] Delete failed:', err?.message);
      toast.error('Delete failed');
    }
  };

  const quickToggle = async (item, field) => {
    try {
      await axios.patch(`${API}/api/admin/case-studies/${item.id}`, { [field]: !item[field] }, { headers: getAuthHeaders() });
      fetchItems();
    } catch (err) {
      console.error('[CaseStudies] Toggle failed:', err?.message);
      toast.error('Update failed');
    }
  };

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      it.org_name?.toLowerCase().includes(q) ||
      it.short_name?.toLowerCase().includes(q) ||
      it.industry?.toLowerCase().includes(q) ||
      it.workshop_category?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="case-studies-manager">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Case Studies & Partners</h2>
          <p className="text-sm text-slate-400 mt-1">
            Manage partner organizations, case studies, and outcomes shown on the public /workshops page.
          </p>
        </div>
        <Button
          onClick={startCreate}
          className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold"
          data-testid="case-studies-new-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New Case Study
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatBox label="Total" value={items.length} />
        <StatBox label="Active" value={items.filter((i) => i.active).length} accent="text-cyan-400" />
        <StatBox label="Featured" value={items.filter((i) => i.featured).length} accent="text-violet-400" />
        <StatBox label="With Logo" value={items.filter((i) => i.logo_url).length} accent="text-emerald-400" />
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, industry, category..."
          className="pl-10 bg-slate-900 border-slate-700 text-white"
          data-testid="case-studies-search"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/50">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Order</th>
              <th className="px-4 py-3 text-left">Logo</th>
              <th className="px-4 py-3 text-left">Organization</th>
              <th className="px-4 py-3 text-left">Industry</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-center">Featured</th>
              <th className="px-4 py-3 text-center">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-500 py-10">No case studies yet. Click "New Case Study" to add one.</td></tr>
            )}
            {filtered.map((item) => (
              <tr key={item.id} className="hover:bg-slate-800/30 transition-colors" data-testid={`case-study-row-${item.short_name || item.id}`}>
                <td className="px-4 py-3 text-slate-400 font-mono text-xs">{item.display_order}</td>
                <td className="px-4 py-3">
                  {item.logo_url ? (
                    <div className="w-14 h-10 rounded bg-white flex items-center justify-center p-1">
                      <img src={item.logo_url} alt={item.org_name} className="max-h-8 max-w-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-14 h-10 rounded bg-slate-800 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-slate-500" />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="text-white font-semibold">{item.org_name}</div>
                  {item.short_name && <div className="text-xs text-slate-500">{item.short_name}</div>}
                </td>
                <td className="px-4 py-3 text-slate-300 text-xs">{item.industry || '—'}</td>
                <td className="px-4 py-3 text-slate-300 text-xs">{item.workshop_category || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => quickToggle(item, 'featured')}
                    className="inline-flex"
                    title={item.featured ? 'Unfeature' : 'Feature'}
                    data-testid={`featured-toggle-${item.short_name || item.id}`}
                  >
                    {item.featured
                      ? <Star className="h-4 w-4 text-violet-400 fill-violet-400" />
                      : <StarOff className="h-4 w-4 text-slate-600" />
                    }
                  </button>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => quickToggle(item, 'active')}
                    className="inline-flex"
                    title={item.active ? 'Set Inactive' : 'Set Active'}
                    data-testid={`active-toggle-${item.short_name || item.id}`}
                  >
                    {item.active
                      ? <Eye className="h-4 w-4 text-cyan-400" />
                      : <EyeOff className="h-4 w-4 text-slate-600" />
                    }
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {item.website_url && (
                      <a href={item.website_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-slate-800 text-slate-400 hover:text-cyan-400" title="Open website">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    <button onClick={() => startEdit(item)} className="p-2 rounded hover:bg-slate-800 text-slate-400 hover:text-violet-400" title="Edit" data-testid={`edit-${item.short_name || item.id}`}>
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(item)} className="p-2 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" title="Delete" data-testid={`delete-${item.short_name || item.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editing && (
        <CaseStudyEditModal
          editing={editing}
          setEditing={setEditing}
          onCancel={cancelEdit}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

function StatBox({ label, value, accent = 'text-white' }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 px-4 py-3">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold ${accent} mt-1`}>{value}</div>
    </div>
  );
}

function CaseStudyEditModal({ editing, setEditing, onCancel, onSave, saving }) {
  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-slate-950 border border-slate-800 rounded-2xl max-w-3xl w-full my-8 overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="case-study-edit-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 sticky top-0 bg-slate-950 z-10">
          <h3 className="text-lg font-bold text-white">
            {editing.id ? 'Edit Case Study' : 'New Case Study'}
          </h3>
          <button onClick={onCancel} className="p-2 rounded hover:bg-slate-800 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Org Info */}
          <Section title="Organization">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Organization Name *">
                <Input value={editing.org_name || ''} onChange={(e) => set('org_name', e.target.value)} data-testid="cs-field-org-name" />
              </Field>
              <Field label="Short Name / Acronym">
                <Input value={editing.short_name || ''} onChange={(e) => set('short_name', e.target.value)} placeholder="CCSD, USAF..." data-testid="cs-field-short-name" />
              </Field>
              <Field label="URL Slug">
                <Input value={editing.slug || ''} onChange={(e) => set('slug', e.target.value)} placeholder="auto-generated from org name" data-testid="cs-field-slug" />
                <p className="text-xs text-slate-500 mt-1">Used in /case-studies/&lt;slug&gt; URL. Leave blank to auto-generate.</p>
              </Field>
              <Field label="Industry">
                <select
                  value={editing.industry || ''}
                  onChange={(e) => set('industry', e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  data-testid="cs-field-industry"
                >
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
              <Field label="Workshop Category">
                <select
                  value={editing.workshop_category || ''}
                  onChange={(e) => set('workshop_category', e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                  data-testid="cs-field-category"
                >
                  <option value="">Select category…</option>
                  {WORKSHOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Logo URL">
                <LogoUploadField
                  value={editing.logo_url || ''}
                  onChange={(v) => set('logo_url', v)}
                  onPublicIdChange={(pid) => set('logo_public_id', pid)}
                />
              </Field>
              <Field label="Website URL">
                <Input value={editing.website_url || ''} onChange={(e) => set('website_url', e.target.value)} placeholder="https://..." data-testid="cs-field-website" />
              </Field>
            </div>
          </Section>

          {/* Engagement */}
          <Section title="Engagement">
            <div className="space-y-4">
              <Field label="Engagement Title">
                <Input value={editing.engagement_title || ''} onChange={(e) => set('engagement_title', e.target.value)} placeholder="e.g., Leadership Development Bootcamp" data-testid="cs-field-engagement-title" />
              </Field>
              <Field label="Audience">
                <Input value={editing.audience || ''} onChange={(e) => set('audience', e.target.value)} placeholder="Students, Executives, Service Members…" data-testid="cs-field-audience" />
              </Field>
              <Field label="Description">
                <Textarea value={editing.description || ''} onChange={(e) => set('description', e.target.value)} rows={3} data-testid="cs-field-description" />
              </Field>
              <Field label="Outcome / Results">
                <Textarea value={editing.outcome || ''} onChange={(e) => set('outcome', e.target.value)} rows={3} placeholder="Use credible, outcome-based language. Avoid fabricated stats." data-testid="cs-field-outcome" />
                <p className="text-xs text-slate-500 mt-1">Outcome-based language only. Do not fabricate metrics.</p>
              </Field>
              <Field label="Hero Image URL">
                <Input value={editing.hero_image || ''} onChange={(e) => set('hero_image', e.target.value)} placeholder="/images/... or https://..." data-testid="cs-field-hero" />
              </Field>
            </div>
          </Section>

          {/* Testimonial */}
          <Section title="Testimonial (Optional)">
            <div className="space-y-4">
              <Field label="Quote">
                <Textarea value={editing.testimonial_quote || ''} onChange={(e) => set('testimonial_quote', e.target.value)} rows={2} placeholder="Direct quote from client…" data-testid="cs-field-quote" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Author Name & Role">
                  <Input value={editing.testimonial_author || ''} onChange={(e) => set('testimonial_author', e.target.value)} placeholder="Jane Smith, Director of Ops" data-testid="cs-field-author" />
                </Field>
                <Field label="Video Testimonial URL (optional)">
                  <Input value={editing.video_url || ''} onChange={(e) => set('video_url', e.target.value)} placeholder="https://youtube.com/..." data-testid="cs-field-video" />
                </Field>
              </div>
            </div>
          </Section>

          {/* Display Settings */}
          <Section title="Display Settings">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Display Order">
                <Input type="number" value={editing.display_order ?? 100} onChange={(e) => set('display_order', parseInt(e.target.value) || 0)} data-testid="cs-field-order" />
                <p className="text-xs text-slate-500 mt-1">Lower = earlier on page</p>
              </Field>
              <Field label="Featured">
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.featured} onChange={(e) => set('featured', e.target.checked)} className="h-4 w-4" data-testid="cs-field-featured" />
                  <span className="text-sm text-slate-300">Show in featured grid</span>
                </label>
              </Field>
              <Field label="Active">
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => set('active', e.target.checked)} className="h-4 w-4" data-testid="cs-field-active" />
                  <span className="text-sm text-slate-300">Visible on public site</span>
                </label>
              </Field>
            </div>
            <Field label="Internal Contact Name (private)">
              <Input value={editing.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} placeholder="Internal use only — not shown publicly" data-testid="cs-field-contact" />
            </Field>
          </Section>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800 sticky bottom-0 bg-slate-950">
          <Button variant="ghost" onClick={onCancel} className="text-slate-300" data-testid="cs-cancel-btn">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold" data-testid="cs-save-btn">
            {saving ? 'Saving…' : <><Check className="h-4 w-4 mr-2" /> Save</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs text-violet-400 font-semibold uppercase tracking-wider mb-3">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function LogoUploadField({ value, onChange, onPublicIdChange }) {
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/admin/case-studies/upload-logo`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      // Cloudinary returns an absolute https:// URL; local fallback returns a relative /api/... path
      const finalUrl = /^https?:\/\//i.test(data.url) ? data.url : `${API}${data.url}`;
      onChange(finalUrl);
      if (onPublicIdChange) onPublicIdChange(data.public_id || '');
      toast.success(
        data.storage === 'cloudinary' ? 'Logo uploaded to Cloudinary' : 'Logo uploaded'
      );
    } catch (err) {
      console.error('[LogoUpload] Failed:', err?.response?.data || err?.message);
      toast.error('Upload failed: ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://… or upload below"
        className="bg-slate-900 border-slate-700 text-white"
        data-testid="cs-field-logo-url"
      />
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid="cs-logo-file-input"
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 h-9 text-xs"
          data-testid="cs-logo-upload-btn"
        >
          {uploading ? 'Uploading…' : '↑ Upload Logo'}
        </Button>
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              if (onPublicIdChange) onPublicIdChange('');
            }}
            className="text-xs text-slate-500 hover:text-red-400"
            data-testid="cs-logo-clear"
          >
            Clear
          </button>
        )}
        <span className="text-xs text-slate-500">PNG, JPG, SVG · max 5 MB</span>
      </div>
      {value && (
        <div className="mt-2 inline-flex p-2 rounded bg-white">
          <img src={value} alt="preview" className="max-h-10 max-w-[160px] object-contain" />
        </div>
      )}
    </div>
  );
}
